import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import PropTypes from 'prop-types'; // Import PropTypes for prop validation
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../landing/formular-sesizare/customToast.css';

// Soft UI Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import ActionRaspunsOficial from "./components/ActionRaspunsOficial";
import ActionActualizareStatus from "./components/ActionActualizareStatus";
import ActionRedirectionareInstitutie from "./components/ActionRedirectionareInstitutie";
import useAPI from "utils/useApi";

import Swal from "sweetalert2";
import Template from "layouts/pages/sweet-alerts/components/Template";

export default function ReportActions({
  reportData,
  status,
  setStatus,
  redirectInstitution,
  setRedirectInstitution,
  redirectReason,
  setRedirectReason,
  message,
  setMessage,
  isStatusSolved,
  sendNotification,
  setSendNotification,
  isMobile,
  refreshDetails
}) {

  const [formActionsData, setFormFetchData] = useState(null);
  const [additionalInfoMessage, setAdditionalInfoMessage] = useState("");
  const [incidentMotivation, setIncidentMotivation] = useState([]); // Array pentru tip incident/motivație
  const [incidentLocation, setIncidentLocation] = useState(""); // "online" sau "offline"
  const [offlineCategories, setOfflineCategories] = useState([]); // Array pentru categorii offline
  const client = useAPI();

  const fetchComplaintFormActions = async () => {
    try {
      const response = await client.getComplaintFornActions();
      setFormFetchData(response);
    } catch (error) {
      console.error("Error fetching form data:", error);
    }
  }
    
  useEffect(() => {
    fetchComplaintFormActions();
  }, []);

  useEffect(() => {
    if (
      formActionsData &&
      reportData &&
      typeof reportData.status !== 'undefined'
    ) {
      const statusChoices = formActionsData.status_choices || [];
      const found = statusChoices.find(
        (opt) => opt.value === reportData.status || opt.label === reportData.status
      );
      const backendStatusValue = found ? found.value : reportData.status;
      if (status !== backendStatusValue) {
        setStatus(backendStatusValue);
      }
    }
  }, [formActionsData, reportData.status]);


  useEffect(() => {
    if (formActionsData && formActionsData.status_choices) {
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('complaint-status-choices', { detail: { status_choices: formActionsData.status_choices } }));
      }
    }
  }, [formActionsData]);


  const handleUpdateStatus = async (additionalInfoMsg = additionalInfoMessage) => {
    if (!reportData?.uuid || !status) return;
    
    // Validate: cannot set status to "Soluționată" / "RESOLVED" without sending a response first
    const isResolved = status.toLowerCase() === "resolved" || status === "RESOLVED";
    if (isResolved) {
      const hasResponse = reportData?.resolved_form && (
        (reportData.resolved_form.resolution_details && reportData.resolved_form.resolution_details.trim() !== "") ||
        (reportData.resolved_form.files && reportData.resolved_form.files.length > 0)
      );
      if (!hasResponse) {
        toast.error("Nu puteți marca sesizarea ca soluționată fără a trimite un răspuns oficial petentului. Vă rugăm să completați secțiunea «Răspuns oficial».");
        return;
      }
    }
    
    // Prepare data object
    const updateData = { status };
    
    // Add additional info message if status is "ADDITIONAL_INFO_REQUIRED"
    if (status && status.toLowerCase() === "additional_info_required" && additionalInfoMsg) {
      updateData.additional_info_details = additionalInfoMsg;
    }
    try {
      await client.updateComplaintFormStatus(reportData.uuid, updateData);
      if (reportData.status !== status) {
        reportData.status = status;
      }
      toast.success('Status actualizat cu succes!')
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('complaint-status-updated', { detail: { status } }));
      }
      // Clear the message after successful update
      setAdditionalInfoMessage("");
    } catch (error) {
      const data = error.response?.data;
      if (data) {
        if (typeof data.detail === "string") {
          toast.error(data.detail);
        } else if (Array.isArray(data.detail)) {
          data.detail.forEach((msg) => toast.error(msg));
        } else if (typeof data === "string") {
          toast.error(data);
        } else if (typeof data === "object") {
          Object.entries(data).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach((msg) => toast.error(`${field}: ${msg}`));
            } else {
              toast.error(`${field}: ${messages}`);
            }
          });
        } else {
          toast.error("A apărut o eroare la actualizarea statusului.");
        }
      } else {
        toast.error("A apărut o eroare la actualizarea statusului. Vă rugăm să încercați mai târziu.");
      }
    }
  };


  const handleRedirectToInstitution = async () => {
    if (!reportData?.uuid || !redirectInstitution) return;
    const data = {
      "redirect_to": redirectInstitution,
      "redirect_reason": redirectReason,
    };
    try {
      await client.registerRedirectComplaintForm(reportData.uuid, data);
      if (reportData.institution_entity !== redirectInstitution) {
        reportData.institution_entity = redirectInstitution;
        
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('complaint-institution-updated', { detail: { institution: redirectInstitution } }));
        }
        toast.success(
          <>
            Redirecționarea a fost efectuată cu succes!
            <br/>
            Te rugăm să aștepți câteva secunde pentru a fi redirecționat către pagina principală.
          </>
        );
        setTimeout(() => {
          window.location.replace("/dashboard/panou-principal")
        }, 3000);

      }
    } catch (error) {
      console.error("Error redirecting complaint:", error);
      const errors = error.response?.data;
      if (errors && typeof errors === 'object') {
        Object.entries(errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach(msg => toast.error(`${field}: ${msg}`));
          } else {
            toast.error(`${field}: ${messages}`);
          }
        });
      } else {
        toast.error(`A apărut o eroare: ${error.message || error}`);
      }
    }
  }

  const [files, setFiles] = useState([]);

  useEffect(() => {
    const resolved = reportData?.resolved_form;
    if (resolved) {
      if (resolved.resolution_details) {
        setMessage(resolved.resolution_details);
      }
      if (resolved.incident_types?.length) {
        setIncidentMotivation(resolved.incident_types);
      }
      if (resolved.incident_place_type) {
        setIncidentLocation(resolved.incident_place_type);
      }
      if (resolved.injury_types?.length) {
        setOfflineCategories(resolved.injury_types);
      }
    }
    // eslint-disable-next-line
  }, [reportData?.resolved_form]);

  return (
    <SoftBox p={2}>
      <ToastContainer 
        position="top-right" 
        autoClose={4000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        rtl={false} 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover
        theme="light"
      />
      <Grid container spacing={2}>
        {/* Col stânga */}
        <Grid item xs={12} md={6}>
          {/* Actualizare Status */}
          <ActionActualizareStatus
            status={status}
            setStatus={setStatus}
            isStatusSolved={isStatusSolved}
            formActionsData={formActionsData}
            handleUpdateStatus={handleUpdateStatus}
            currentStatus={reportData.status} // pass backend status
            additionalInfoMessage={additionalInfoMessage}
            setAdditionalInfoMessage={setAdditionalInfoMessage}
          />

          {/* Redirecționare */}
          <ActionRedirectionareInstitutie
            redirectInstitution={redirectInstitution}
            setRedirectInstitution={setRedirectInstitution}
            redirectReason={redirectReason}
            setRedirectReason={setRedirectReason}
            isStatusSolved={isStatusSolved}
            formActionsData={formActionsData}
            handleRedirectToInstitution={handleRedirectToInstitution}
            currentInstitution={reportData.institution_entity}
          />
        </Grid>

        {/* Col dreapta */}
        <Grid item xs={12} md={6}>
          <ActionRaspunsOficial
              reportId={reportData.uuid}
              resolvedForm={reportData.resolved_form}
              files={files}
              setFiles={setFiles}
              message={message}
              setMessage={setMessage}
              isStatusSolved={isStatusSolved}
              refreshDetails={refreshDetails}
              incidentMotivation={incidentMotivation}
              setIncidentMotivation={setIncidentMotivation}
              incidentLocation={incidentLocation}
              setIncidentLocation={setIncidentLocation}
              offlineCategories={offlineCategories}
              setOfflineCategories={setOfflineCategories}
              formActionsData={formActionsData}
          />
        </Grid>
      </Grid>
    </SoftBox>
  );
}

ReportActions.propTypes = {
  reportData: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  setStatus: PropTypes.func.isRequired,
  redirectInstitution: PropTypes.string.isRequired,
  setRedirectInstitution: PropTypes.func.isRequired,
  redirectReason: PropTypes.string.isRequired,
  setRedirectReason: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  setMessage: PropTypes.func.isRequired,
  isStatusSolved: PropTypes.bool.isRequired,
  sendNotification: PropTypes.bool.isRequired,
  setSendNotification: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  refreshDetails: PropTypes.func,
};
